import { useState, useEffect } from "react";
import {
    isRoofMeasurementKey,
    buildEmptyMeasurements,
} from "../../../constants/roofMeasurements";
import { useInspectionAttributes } from "../../../hooks/query";
import { parseMeasurementAttributeValue } from "../../../utils/measurementAttribute";
import { useEstimateContractContextManager } from ".";

export function useInit({ projectId }: { projectId?: string }) {
    const { setValues, setReferenceValue, setInitLoading } =
        useEstimateContractContextManager();
    const [error, setError] = useState<string | null>(null);

    const { useListByProjectId } = useInspectionAttributes();
    const useListByProjectIdSvc = useListByProjectId();

    const loadInitialData = async (id: string, isCancelled?: () => boolean) => {
        setInitLoading(true);
        setError(null);
        try {
            const attributesRes = await useListByProjectIdSvc.mutateAsync({
                ProjectId: id,
            });
            if (isCancelled?.()) return { success: false };

            const { measurements, attributes, attributeIds } =
                parseInspectionAttributes(attributesRes);

            const applyLoadedData = (prev: any) => ({
                ...prev,
                measurements,
                attributes,
                attributeIds,
            });

            setValues(applyLoadedData);
            setReferenceValue(applyLoadedData);

            return { success: true };
        } catch (err: any) {
            if (!isCancelled?.()) {
                setError(err.message || "Failed to load data");
            }
            return { success: false, error: err };
        } finally {
            if (!isCancelled?.()) {
                setInitLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!projectId) return;

        let cancelled = false;
        loadInitialData(projectId, () => cancelled);

        return () => {
            cancelled = true;
        };
    }, [projectId]);

    return {
        loadInitialData,
        error,
    };
}

function parseInspectionAttributes(attributesRes: any) {
    const Measurements = buildEmptyMeasurements();
    const attributes: any = {};
    const attributeIds: Record<string, string> = {};

    if (attributesRes && Array.isArray(attributesRes)) {
        attributesRes.forEach((attr: any) => {
            if (!attr.AttributeName) return;

            const name = attr.AttributeName;
            if (attr.Id) {
                attributeIds[name] = attr.Id;
            }

            if (isRoofMeasurementKey(name)) {
                Measurements[name] = parseMeasurementAttributeValue(
                    attr.AttributeValue,
                );
                return;
            }

            const value = attr.AttributeValue;
            let parsedValue: any = value;
            if (typeof value === "string" && value.trim() !== "") {
                try {
                    parsedValue = JSON.parse(value);
                } catch {
                    const num = Number(value);
                    if (!isNaN(num)) {
                        parsedValue = num;
                    }
                }
            }

            attributes[name] = parsedValue ?? 0;
        });
    }

    return {
        measurements: {
            ...buildEmptyMeasurements(),
            ...Measurements,
        },
        attributes,
        attributeIds,
    };
}
